/**
 * useScrumMaster Hook
 * @implements FS-005.8.7 - Client Architecture (MobX Observable Store)
 * @implements FS-005.8.8 - GraphQL Subscription Protocol
 * @implements US-057 - Client MobX Store + Subscription Wiring
 * @implements BS-005.22 - Error Semantics
 * @implements FS-003.2.2 - Rich Mutation Responses
 *
 * Custom React hook that bridges the KanbanStore MobX observable with Apollo GraphQL.
 * All tiers use subscription-driven real-time updates (push benefits all clients).
 *
 * Uses raw apolloClient.subscribe() (not useSubscription hook) to avoid
 * double-renders with MobX observer().
 */

import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useApolloClient, gql } from '@apollo/client';
import {
  KanbanStore,
  type KanbanRepresentationPayload,
} from '@/stores/KanbanStore';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Fragment for KanbanTicket fields shared by query, subscription, and mutations.
 * @implements TS-056.1 - KanbanTicket SDL type
 */
const KANBAN_TICKET_FIELDS = gql`
  fragment KanbanTicketFields on KanbanTicket {
    id
    type
    title
    column
    lifecycleState
    parentId
    children {
      id
      text
    }
    specReferences
    acceptanceCriteria
    description
    context
    validationFeedback {
      agentType
      status
      feedback
      timestamp
    }
  }
`;

/**
 * Fragment for full KanbanRepresentation fields.
 * Shared between query, subscription, and mutation response selections.
 * @implements FS-005.8.8 - GraphQL Subscription Protocol
 * @implements FS-003.2.2 - Rich Mutation Responses
 */
const KANBAN_REPRESENTATION_FIELDS = gql`
  ${KANBAN_TICKET_FIELDS}
  fragment KanbanRepresentationFields on KanbanRepresentation {
    tickets {
      ...KanbanTicketFields
    }
    parentColumns {
      ticketId
      column
    }
    validationStatuses {
      ticketId
      status
    }
    ticketsByColumn {
      column
      tickets {
        ...KanbanTicketFields
      }
    }
  }
`;

/**
 * Query for initial kanban state load and reconnection recovery.
 * @implements TS-056.3 - kanbanState query
 */
const KANBAN_STATE_QUERY = gql`
  ${KANBAN_REPRESENTATION_FIELDS}
  query KanbanState($projectId: ID!) {
    kanbanState(projectId: $projectId) {
      ...KanbanRepresentationFields
    }
  }
`;

/**
 * Subscription for real-time kanban state changes.
 * Each event is a self-contained full state snapshot.
 * @implements TS-056.2 - kanbanStateChanged subscription
 */
const KANBAN_STATE_CHANGED_SUBSCRIPTION = gql`
  ${KANBAN_REPRESENTATION_FIELDS}
  subscription KanbanStateChanged($projectId: ID!) {
    kanbanStateChanged(projectId: $projectId) {
      ...KanbanRepresentationFields
    }
  }
`;

/**
 * Mutation to move a ticket to a new kanban column.
 * Includes state and errorType for rich mutation responses.
 * @implements TS-055.8 - Move Ticket Mutation
 * @implements FS-003.2.2 - Rich Mutation Responses
 * @implements BS-005.22 - Error Semantics
 */
const MOVE_TICKET_MUTATION = gql`
  ${KANBAN_REPRESENTATION_FIELDS}
  mutation MoveTicket(
    $projectId: String!
    $ticketId: String!
    $toColumn: KanbanColumn!
  ) {
    moveTicket(
      projectId: $projectId
      ticketId: $ticketId
      toColumn: $toColumn
    ) {
      success
      rejection {
        reason
        details
      }
      errorType
      state {
        ...KanbanRepresentationFields
      }
    }
  }
`;

/**
 * Mutation to submit validation feedback for a ticket.
 * Includes state and errorType for rich mutation responses.
 * @implements TS-055.9 - Submit Validation Feedback Mutation
 * @implements FS-003.2.2 - Rich Mutation Responses
 * @implements BS-005.22 - Error Semantics
 */
const SUBMIT_VALIDATION_FEEDBACK_MUTATION = gql`
  ${KANBAN_REPRESENTATION_FIELDS}
  mutation SubmitValidationFeedback(
    $projectId: String!
    $ticketId: String!
    $feedback: ValidationFeedbackInput!
  ) {
    submitValidationFeedback(
      projectId: $projectId
      ticketId: $ticketId
      feedback: $feedback
    ) {
      success
      rejection {
        reason
        details
      }
      errorType
      state {
        ...KanbanRepresentationFields
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * Input for the useScrumMaster hook.
 * @implements US-057 - Client MobX Store + Subscription Wiring
 */
export interface UseScrumMasterInput {
  projectId: string;
}

/**
 * Hook return type.
 * @implements US-057 - Client MobX Store + Subscription Wiring
 */
export interface UseScrumMasterResult {
  /** The observable store (components use observer() to react) */
  store: KanbanStore;
  /** Move a ticket to a new kanban column (with optimistic UI) */
  moveTicket: (ticketId: string, toColumn: string) => Promise<void>;
  /** Submit validation feedback for a ticket */
  submitValidationFeedback: (
    ticketId: string,
    feedback: {
      agentType: string;
      status: string;
      feedback?: string;
      timestamp: string;
    },
  ) => Promise<void>;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Custom hook for managing kanban state via KanbanStore + Apollo.
 *
 * Behavior:
 * 1. Creates a KanbanStore instance (memoized per projectId)
 * 2. Fetches initial state via kanbanState query
 * 3. Subscribes to kanbanStateChanged via raw apolloClient.subscribe()
 * 4. Writes each subscription event to store.server via updateFromServer
 * 5. On unmount: unsubscribes
 *
 * @implements FS-005.8.7 - Client Architecture (MobX Observable Store)
 * @implements FS-005.8.8 - GraphQL Subscription Protocol
 * @implements US-057 - Client MobX Store + Subscription Wiring
 */
export const useScrumMaster = (
  input: UseScrumMasterInput,
): UseScrumMasterResult => {
  const apolloClient = useApolloClient();

  // Create store instance (memoized per projectId)
  const store = useMemo(() => new KanbanStore(), [input.projectId]);

  // Ref to track mount state for async operations
  const isMountedRef = useRef(true);

  // ============================================
  // Shared: Fetch initial state
  // ============================================

  const fetchKanbanState = useCallback(async () => {
    try {
      const { data } = await apolloClient.query<{
        kanbanState: KanbanRepresentationPayload | null;
      }>({
        query: KANBAN_STATE_QUERY,
        variables: { projectId: input.projectId },
        fetchPolicy: 'network-only',
      });

      if (isMountedRef.current && data.kanbanState) {
        store.updateFromServer(data.kanbanState);
      }
    } catch (err) {
      if (isMountedRef.current) {
        store.setError(
          err instanceof Error ? err.message : 'Failed to fetch kanban state',
        );
      }
    }
  }, [apolloClient, input.projectId, store]);

  // ============================================
  // Mount Effect: Initial fetch + subscription/polling
  // ============================================

  useEffect(() => {
    isMountedRef.current = true;

    // Initial state fetch
    const initFetch = async () => {
      store.setLoading(true);
      await fetchKanbanState();
      if (isMountedRef.current) {
        store.setLoading(false);
      }
    };

    initFetch();

    // Subscribe to real-time updates
    const subscription = apolloClient
      .subscribe<{
        kanbanStateChanged: KanbanRepresentationPayload;
      }>({
        query: KANBAN_STATE_CHANGED_SUBSCRIPTION,
        variables: { projectId: input.projectId },
      })
      .subscribe({
        next: ({ data }) => {
          if (isMountedRef.current && data?.kanbanStateChanged) {
            store.updateFromServer(data.kanbanStateChanged);
          }
        },
        error: (err) => {
          if (isMountedRef.current) {
            store.setError(
              err instanceof Error ? err.message : 'Subscription error',
            );
          }
        },
      });

    return () => {
      isMountedRef.current = false;
      subscription.unsubscribe();
    };
  }, [apolloClient, input.projectId, store, fetchKanbanState]);

  // ============================================
  // Action Functions (with GraphQL mutations)
  // ============================================

  /**
   * Move a ticket to a new kanban column.
   * Optimistic UI flow:
   * 1. Set pending move in local state (immediate visual feedback)
   * 2. Fire mutation to server
   * 3. On resolve: clear pending move (subscription delivers authoritative state)
   * 4. On error: clear pending move + set structured error
   *
   * @implements FS-005.8.7 - Optimistic UI flow
   * @implements FS-003.2.2 - Rich Mutation Responses
   * @implements BS-005.22 - Error Semantics
   */
  const moveTicket = useCallback(
    async (ticketId: string, toColumn: string): Promise<void> => {
      // Step 1: Optimistic update
      store.setPendingMove(ticketId, toColumn);

      try {
        // Step 2: Fire mutation
        const { data } = await apolloClient.mutate({
          mutation: MOVE_TICKET_MUTATION,
          variables: {
            projectId: input.projectId,
            ticketId,
            toColumn,
          },
        });

        // Step 3: Clear pending move on resolution
        store.clearPendingMove(ticketId);

        // Handle error responses with structured error display
        if (!data?.moveTicket?.success) {
          const errorType = data?.moveTicket?.errorType ?? null;
          const rejectionReason = data?.moveTicket?.rejection?.reason ?? null;
          store.setStructuredError(errorType, rejectionReason);
          return;
        }

        // Apply mutation response for immediate consistency
        // (subscription will also deliver the state)
        if (data?.moveTicket?.state) {
          store.updateFromServer(data.moveTicket.state);
        }
      } catch (err) {
        // Step 4: Clear pending move + set error
        store.clearPendingMove(ticketId);
        store.setError(
          err instanceof Error ? err.message : 'Failed to move ticket',
        );
      }
    },
    [apolloClient, input.projectId, store],
  );

  /**
   * Submit validation feedback for a ticket.
   * @implements FS-005.2.2 - Consolidation Validation Phase
   * @implements FS-003.2.2 - Rich Mutation Responses
   * @implements BS-005.22 - Error Semantics
   */
  const submitValidationFeedback = useCallback(
    async (
      ticketId: string,
      feedback: {
        agentType: string;
        status: string;
        feedback?: string;
        timestamp: string;
      },
    ): Promise<void> => {
      try {
        const { data } = await apolloClient.mutate({
          mutation: SUBMIT_VALIDATION_FEEDBACK_MUTATION,
          variables: {
            projectId: input.projectId,
            ticketId,
            feedback,
          },
        });

        // Handle error responses with structured error display
        if (!data?.submitValidationFeedback?.success) {
          const errorType = data?.submitValidationFeedback?.errorType ?? null;
          const rejectionReason =
            data?.submitValidationFeedback?.rejection?.reason ?? null;
          store.setStructuredError(errorType, rejectionReason);
          return;
        }

        // Apply mutation response state for immediate consistency
        if (data?.submitValidationFeedback?.state) {
          store.updateFromServer(data.submitValidationFeedback.state);
        }
      } catch (err) {
        store.setError(
          err instanceof Error ? err.message : 'Failed to submit feedback',
        );
      }
    },
    [apolloClient, input.projectId, store],
  );

  // ============================================
  // Return Values
  // ============================================

  return {
    store,
    moveTicket,
    submitValidationFeedback,
  };
};
